// src/components/Branch/config/sections/CollectionsSection.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CollectionIcon,
  PencilIcon,
  CheckCircleIcon,
} from "@heroicons/react/outline";
import { updateCollectionsThunk } from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

const JEWELRY_CATEGORIES = [
  { _id: "GOLD_JEWELRY", name: "Gold Jewelry" },
  { _id: "DIAMOND_JEWELRY", name: "Diamond Jewelry" },
  { _id: "SILVER_JEWELRY", name: "Silver Jewelry" },
  { _id: "PLATINUM_JEWELRY", name: "Platinum Jewelry" },
  { _id: "BRIDAL", name: "Bridal Collection" },
  { _id: "KUNDAN", name: "Kundan" },
  { _id: "POLKI", name: "Polki" },
  { _id: "ANTIQUE", name: "Antique" },
  { _id: "TEMPLE", name: "Temple Jewelry" },
  { _id: "PEARLS", name: "Pearls" },
  { _id: "GEMSTONES", name: "Gemstones" },
  { _id: "CHAINS", name: "Chains" },
  { _id: "COINS", name: "Coins & Bars" },
];

const CollectionsSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { collections, loadingSection } = useSelector((state) => state.branchDisplay);

  const [isEditing, setIsEditing] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);

  useEffect(() => {
    if (collections) {
      setSelectedCollections(collections.map((c) => c.category));
    }
  }, [collections]);

  const handleToggle = (categoryId) => {
    setSelectedCollections((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSave = async () => {
    const collectionsData = selectedCollections.map((category) => {
      const existing = collections.find((c) => c.category === category);
      return existing || { category, isActive: true, isFeatured: false };
    });

    await dispatch(updateCollectionsThunk({ branchCode, collections: collectionsData }));
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium flex items-center">
            <CollectionIcon className="h-4 w-4 mr-2" />
            Jewelry Collections
          </h4>
          {isRootAdmin && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {JEWELRY_CATEGORIES.map((category) => (
                <label
                  key={category._id}
                  className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer border ${
                    selectedCollections.includes(category._id)
                      ? "bg-blue-50 border-blue-300"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedCollections.includes(category._id)}
                    onChange={() => handleToggle(category._id)}
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm">{category.name}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={loadingSection === "collections"}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Save Collections
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {collections.length === 0 ? (
              <p className="text-gray-500">No collections selected</p>
            ) : (
              collections
                .filter((c) => c.isActive)
                .map((collection, index) => (
                  <span
                    key={index}
                    className={`px-3 py-1.5 rounded-full text-sm ${
                      collection.isFeatured
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {JEWELRY_CATEGORIES.find((c) => c._id === collection.category)?.name ||
                      collection.category}
                    {collection.isFeatured && " ⭐"}
                  </span>
                ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionsSection;
